<template>
  <div class="ic-cornerstone ic-cornerstone--bg-grey">
    <wl-cornerstone-banner
      title="Criminal Records Check"
      :banner-image="bannerImage"
      :prefill-fields="$parent.$attrs['prefill-fields']"
      :fallback-fields="cms.page.banner.fallback_fields"
      :search-tabs="cms.page.banner.search_tabs"
      :button-icon="cms.page.banner.button_icon"
      :search-icon-size="cms.page.banner.button_icon_size"
      :review-text="cms.page.banner.review_text"
      :secured-text="cms.page.banner.secured_text"
      :rating-image="ratingImage"
      :people-url="peopleSearchLink"
      :has-trailing-slash="true"
      :default-state-option-label="cms.page.banner.default_state_option_label"
    />
    <div class="ic-cornerstone__section ic-cornerstone__section--copy">
      <h2 v-text="`Criminal Record Search`" />
      <p
        v-text="
          `Criminal records are a type of public record. They include documents regarding crimes committed, time spent in jail, and the results of court cases. While criminal records are public and possible to find, that doesn't mean people will openly tell you about them.`
        "
      />
      <p
        v-text="
          `It's also important to note that more people than you think have some type of criminal record. In fact, you might have one without realizing it. Therefore, it's always a good idea to be up to date on your records.`
        "
      />
      <p
        v-text="
          `Furthermore, looking up this information is more important than ever, since about 70 to 100 million people in the United States have some type of criminal record: that's a third of the population.`
        "
      />
      <p
        v-text="
          `The good news is that we're here to help. Our criminal records search tool will help you learn about your records and the current records of anyone you search for.`
        "
      />
      <h2 v-text="`What Is a Criminal Record?`" />
      <p
        v-text="
          `A criminal record relates to anyone who has committed a crime in the past and can go as far back as 10 years. Finding out someone's criminal record can be extremely useful for keeping your family and friends safe, checking the history of your online date, or even just wanting to ensure your child is in safe hands when they play at your neighbor’s house.`
        "
      />
      <p
        v-text="
          `Criminal records show formal reports of people who have been convicted of offenses, whether they've pleaded guilty or been found guilty. On the contrary, if the person has come out innocent it will not show up anywhere.`
        "
      />
      <p
        v-text="
          `Typically, the police will ask for the person's consent before releasing the criminal record, but like with everything, there are exceptions.`
        "
      />
      <h3 v-text="`Why Is a Criminal Record Useful?`" />
      <p
        v-text="
          `If you're utilizing a people search, you would be interested to know that criminal records are amongst the most highly sought-after pieces of information out there.`
        "
      />
      <p
        v-text="
          `A criminal record is useful for finding out a person's trustworthiness. It is used by potential employers, police, leaders, and individuals who want to find out all there is to know about someone's past convictions.`
        "
      />
      <p
        v-text="
          `People may need to seek a criminal record if they are applying for a job or a visa overseas, or if they are looking to work with children.`
        "
      />
      <p
        v-text="
          `A criminal record is one of the most crucial pieces of information you can find out about someone before deciding whether to get closer to them or distance yourself altogether. It can help you to make the best decision when it comes to your safety and well-being.`
        "
      />
      <p
        v-text="
          `Information.com saves you time by gathering criminal records from public databases and sources, and best of all - it's confidential.`
        "
      />
      <p
        v-text="`A criminal record shows the following important information:`"
      />
      <ul class="ic-cornerstone__list-row ic-cornerstone__list-row--four-col">
        <li>Arrests (old and recent)</li>
        <li>Mugshots</li>
        <li>Police records</li>
        <li>Court records</li>
      </ul>
      <div class="ic-cornerstone__content ic-cornerstone__content--bg-blue">
        <h2 v-text="`How Do You Obtain a Criminal Record?`" />
        <p
          v-text="
            `You can obtain a criminal record by going to the courthouse yourself, or by submitting a request to the custodian. However, there is a much easier and faster method: Information.com. Here you can do a deep and detailed people search of anyone, from an old classmate to a potential love interest.`
          "
        />
        <p
          v-text="
            `Running a criminal records check on Information.com is easy, effortless, and fast, and provides the most in-depth results.`
          "
        />
      </div>
      <div class="ic-cornerstone__content">
        <h2 v-text="`Why Are Criminal Records So Important?`" />
        <p
          v-text="
            `As previously stated, criminal records tell you crucial information about a person that they perhaps don't want to disclose themselves. You may be surprised to know that one in three Americans have a criminal record, which ranges from minor offenses to serious felonies.`
          "
        />
        <p
          v-text="
            `Therefore, for your peace of mind and safety, you're better off finding out the background of the person in question, thus saving yourself from potential danger.`
          "
        />
      </div>
      <h2 v-text="`Why Use Information.com’s Criminal Record Check`" />
      <p
        v-text="
          `Through Information.com, you can easily and effortlessly download the criminal records and background report of the person you are most curious about. The highly accurate information will include the person's contact details, date of birth, home address, marital status, social media accounts, and more.`
        "
      />
      <p
        v-text="
          `Finding out extensive information about a person has never been easier!`
        "
      />
      <h2 v-text="`Safe. Trustworthy. Guarantees Peace of Mind.`" />
      <p
        v-text="
          `Information.com will help you track down anyone you know just by using their first and last name. It's that simple! Long gone are the days when you needed to use a phonebook, visit a courthouse, or undertake hours on end of internet browsing to find public records of that one person.`
        "
      />
      <p
        v-text="
          `Information.com is an easy-to-use and extensive people search platform that delivers reliable and highly accurate results. You put in the person's name, and we do all the work to ensure you have the most up-to-date information about their criminal background.`
        "
      />
      <h2 v-text="`Is There Anything Else Information.com Can Do?`" />
      <p
        v-text="
          `In addition to finding criminal history, Information.com can also help you to find civil records, bankruptcies, liens, lawsuits, arrest reports, traffic reports, and sexual offenses. No information will be left out!`
        "
      />
      <p
        v-text="
          `Find out about what Information.com offers in more detail below.`
        "
      />
      <div class="ic-cornerstone__staggered-columns">
        <ul class="ic-cornerstone__list ic-cornerstone__list--staggered">
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Civil Records`" />
            <p
              v-text="
                `Similar to criminal records, civil records provide a lot of information about a person. However, they are different in that they show non-criminal information, such as restraining orders, child custody hearings, and personal injury cases.`
              "
            />
            <p
              v-html="
                `Information.com provides information about civil
            records when you run a <a href='/public-records-search/'>public records search</a>. Using a
            people search may also yield similar results, so the power to choose
            how you search is always in your hands.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Liens`" />
            <p
              v-text="
                `Another important form of information regarding someone's financial situation is liens, which occur when someone is unable to pay debts, and as a result, their house, property, or car is seized until they pay up the money owed.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Phone Book Listings`" />
            <p
              v-html="
                `By providing the first and last name (or phone number)
            of the person you're curious about, our online search platform will
            find the relevant records. This will save you time and frustration
            that would otherwise be spent trying to find them the old-fashioned
            way through a phonebook. Tools like our
            <a href='/reverse-phone-lookup/'>phone lookup tool</a> will help you find any information
            you need about a phone number.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Sexual Offenses`" />
            <p
              v-text="
                `For ensuring the safety of you and your loved ones, a sexual offenses history will be one big leap forward. A sexual offense status will show if the person in question is on the sex offender registry, whether they just moved in next door to you or they’re someone you’ve met on a dating app.`
              "
            />
            <p
              v-text="
                `It will not only save you time and effort searching by yourself, but it will also save you a whole lot of stress, as you just never know who you are truly dealing with until you know about their background in detail.`
              "
            />
          </li>
        </ul>
        <ul class="ic-cornerstone__list ic-cornerstone__list--staggered">
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Bankruptcies`" />
            <p
              v-text="
                `Finding out if someone has been or is currently bankrupt is up there among the most highly critical things you can do before entering a business with a potential partner or starting a new relationship.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Lawsuits`" />
            <p
              v-text="
                `Lawsuits are not always openly available to the public, but they are almost always available on a public record. They include any lawsuits filed against the person in question, as well as lawsuits that person has filed against someone else.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Arrest Reports`" />
            <p
              v-text="
                `An arrest report, aka an arrest log, is a legal document that states details of an individual’s arrest. A public records search will show any arrests made of the person in question.`
              "
            />
            <p
              v-text="
                `It's important to note that arrest reports are not free, unlike most public reports; however, they tend to be very affordable. So, if you think it's worth it to find out that extra bit of information, then go ahead! You will thank yourself in the long run.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Traffic Reports`" />
            <p
              v-text="
                `Collisions, traffic offenses, and violations (including speeding tickets and DUIs) will show up in a public records search.`
              "
            />
          </li>
          <li class="ic-cornerstone__list-item">
            <h3 v-text="`Full Background Checks`" />
            <p
              v-html="
                `Want to learn everything you can about yourself or
            someone in your life? We have you covered. Our
            <a href='/background-checks-search/'>background check tool</a> can help you find public and
            criminal records about someone, like their name, employment history,
            and much more.`
              "
            />
          </li>
        </ul>
      </div>
      <h2
        v-text="`Bottomline: Fast, Free, Accurate Results | 100% Confidential`"
      />
      <p
        v-text="
          `By obtaining a criminal record through Information.com, you can find out crucial information about a person you are curious about, whether they are your next-door neighbor, or someone who is in close contact with your family and loved ones.`
        "
      />
      <p
        v-text="
          `You can feel more confident about your safety and the safety of others when you find out everything there is to know about someone’s criminal history, as well as other parts of their background they may try to hide.`
        "
      />
      <p
        v-text="
          `Looking up criminal records is a big part of the puzzle when it comes to deciding whether to get close to an individual or protect your safety by distancing yourself, and therefore not worth passing upon.`
        "
      />
    </div>
    <div
      class="ic-cornerstone__section ic-cornerstone__section--people-directory"
    >
      <div class="ic-cornerstone__container">
        <h2 v-text="`Information.com People Directory`" />
        <p
          v-text="
            ` Browse the free people search list below to find colleagues, classmates, friends, neighbors and relatives. Also view our trending names and top names list!`
          "
        />
        <ic-people-directory
          class="ic-people-directory--cornerstone"
          v-for="cardList in renderPeopleDirectory()"
          key="directory"
          :card-list="cardList"
        />
      </div>
    </div>
  </div>
</template>

<script>
// Components
import WlCornerstoneBanner from '@/components/shared/wl-cornerstone-banner';
import IcPeopleDirectory from '@/sections/ic-people-directory';
// Helpers
import Meta from '@/assets/js/shared/misc/meta.mjs';
import getPeopleDirectoryData from '@/assets/js/shared/helpers/getPeopleDirectoryData';
// Images
import bannerImage from '@/assets/images/cornerstone/criminal-records-check.webp';
import ratingImage from '@/assets/images/shared/rating.svg';

export default {
  components: {
    WlCornerstoneBanner,
    IcPeopleDirectory
  },
  data() {
    return {
      bannerImage,
      ratingImage
    };
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          page: require(`@/assets/cms/pages/cornerstone.json`),
          global: require(`@/assets/cms/global.json`)
        };
      }
    }
  },
  methods: {
    renderPeopleDirectory() {
      return this.people && this.people.length > 0 ? [this.people] : [];
    }
  },
  computed: {
    peopleSearchLink() {
      const { query } = this.$route;
      const link = this.cms.page.banner.people_url;
      const replaceLanding = '23um01';
      const redirectLanding = this.cms.global.default_landing_id || '8170';

      if (query.utm_source === 'google') {
        return link.replace(replaceLanding, redirectLanding);
      }

      return link;
    }
  },
  async asyncData({ $bugsnag }) {
    if (!process.server) {
      return;
    }

    return getPeopleDirectoryData($bugsnag);
  },
  head() {
    return {
      title: this.cms.page.criminal_records.page_title,
      meta: this.cms.page.criminal_records.meta
        ? Meta.convertToNuxtMeta(this.cms.page.criminal_records.meta)
        : []
    };
  }
};
</script>
